import React, { useMemo } from 'react';
import { sortedIndex, throttle, uniq } from 'lodash';
import { AbstractHoverZoneProps } from './types';
import { AbstractHoverZoneBase } from './AbstractHoverZoneBase';

export const AbstractHoverZone = <D,>({
  dataPoints,
  xScale,
  getInvertedXScaleValue,
  dataToX,
  tooltipX,
  setTooltipX,
  setTooltipData,
  children,
  ...rest
}: AbstractHoverZoneProps<D>) => {
  const sortedUniqueX = useMemo(
    () => uniq(dataPoints.map(dataToX)).sort((a, b) => a - b),
    [dataPoints, dataToX],
  );
  const dataByX = useMemo(() => {
    const fullMap: { [key: number]: D[] } = {};
    dataPoints.forEach((data: D) => {
      const currTime = dataToX(data);
      if (fullMap[currTime] === undefined) fullMap[currTime] = [];
      fullMap[currTime]?.push(data);
    });
    return fullMap;
  }, [dataPoints, dataToX]);

  // Throttle the mouse move event to 16ms to avoid
  const throttleMouseMove = useMemo(
    () =>
      throttle((e, getInvertedXScaleValue, xScale, sortedUniqueX, dataByX, elementSize) => {
        if (!elementSize) return;
        const { left } = elementSize;
        const x = e.pageX - left;
        // Step 1: Figure out proper X value
        // when in between two values, figure out if we should show the previous or next value:
        // inverted time is a point in between the two values: (x)
        // c is the center: (next - previous * 0.5)
        // p is the previous
        // if x-p > c-p, show the tooltip for the next
        // p------c---x--| -> show next
        // p---x--c------| -> show previous
        const inverted = getInvertedXScaleValue(x, xScale);
        const newIndex = sortedIndex(sortedUniqueX, inverted);
        const previous = sortedUniqueX[newIndex - 1];
        const next = sortedUniqueX[newIndex];
        const offset = inverted - previous > (next - previous) * 0.5 ? 0 : -1;
        const currUniqueX = sortedUniqueX[newIndex + offset] ?? sortedUniqueX[0];
        const currData = dataByX[currUniqueX];
        const xValue = xScale(currUniqueX);
        setTooltipX(xValue);
        setTooltipData(currData);
      }, 16),
    [setTooltipX, setTooltipData],
  );

  return (
    <AbstractHoverZoneBase
      data-component="AbstractHoverZone"
      {...rest}
      onMouseLeave={() => {
        throttleMouseMove.cancel();
        setTooltipData(null);
        setTooltipX(undefined);
      }}
      onMouseMove={(e, elementOffset) => {
        throttleMouseMove(e, getInvertedXScaleValue, xScale, sortedUniqueX, dataByX, elementOffset);
      }}
      tooltipX={tooltipX}
      tooltipY={0}
      positionKey={`${tooltipX}`}
    >
      {children}
    </AbstractHoverZoneBase>
  );
};
